import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import {
    BlockVariant,
    BlockVariantBaseProps,
    BlockVariantProps,
    BlockVariantSide,
    getVariantCss,
    variantDefaultProps,
} from "@tCentro/Components/Common/Block/variant";
import { COMMON, TYPOGRAPHY } from "@tCentro/theme/design";
import { siteWidthStyles } from "@tCentro/theme/mixins";
import breakpointValuesMediaQueries from "@tCentro/utilities/breakpointValuesMediaQueries";
import React from "react";
import styled, { css } from "styled-components";

// -- INTERFACES

export interface BlockBorderProps {
    width: string;
    type: string;
    color: string;
}

export interface CENPDPBlockBaseProps {
    fullWidth?: boolean;
    variant?: BlockVariant;
    variantProps?: BlockVariantProps;
    variantSide?: BlockVariantSide;
    margin?: Array<string | null>;
    preserveSpacing?: boolean;
    paddingBlockPreserveSpacing?: boolean;
}

export interface CENPDPBlockProps extends CENPDPBlockBaseProps, BlockVariantBaseProps { }

// -- UTILS

const parseProps = ({
    fullWidth,
    variant,
    variantProps,
    variantSide,
    margin,
    preserveSpacing,
    containerCss,
    spacerCss,
    wrapperCss,
    paddingBlockPreserveSpacing,
    ...otherProps
}: Partial<CENPDPBlockProps>) => {
    const blockProps: CENPDPBlockProps = {
        fullWidth,
        variant,
        variantProps,
        variantSide,
        margin,
        preserveSpacing,
        containerCss,
        spacerCss,
        wrapperCss,
        paddingBlockPreserveSpacing,
    };

    return { blockProps, otherProps };
};

// -- COMPONENTS

export const BlockWrapper = styled.div<CENPDPBlockProps>`
    ${props => props.wrapperCss}
    ${getVariantCss("wrapperCss")}
`;

export const BlockSpacer = styled.div<CENPDPBlockProps>`
    ${siteWidthStyles()}

    ${props =>
        breakpointValuesMediaQueries(
            props.theme,
            [{ values: props.margin ?? [], properties: ["margin-top", "margin-bottom"] }],
            mediaOptions.min,
        )}

    ${props =>
        props.fullWidth
            ? css`
                  max-width: none;
              `
            : null}
    ${props => props.spacerCss}
    ${getVariantCss("spacerCss")}
`;

const BlockContainerBase = styled.div<CENPDPBlockProps>``;
export const BlockContainer = styled(BlockContainerBase)`
    ${({ preserveSpacing, paddingBlockPreserveSpacing }) => {
        return css`
            > ${BlockSpacer} {
                    ${preserveSpacing
                        ? css`
                              margin-top: ${TYPOGRAPHY.spacing.base};
                              margin-bottom: ${TYPOGRAPHY.spacing.base};
                              ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                            padding-left: 25px;
                                            padding-right: 25px;
                                        `,
                                        css`
                                            padding-left: 25px;
                                            padding-right: 25px;
                                        `,
                                        null,
                                        null,
                                    ],
                                    "max",
                                )}
                          `
                : null}
                ${paddingBlockPreserveSpacing ? css`
                    padding-left: 0px;
                    padding-right: 0px;
                    margin: 0px;
                    ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        css`
                                            padding-left: 0px;
                                            padding-right: 0px;
                                            margin: 0px;
                                        `,
                                        css`
                                            padding-left: 0px;
                                            padding-right: 0px;
                                            margin: 0px;
                                        `,
                                        null,
                                        null,
                                    ],
                                    "max",
                                )}
                ` : null}
                }
            ${BlockContainerBase} & {
                &:first-child > ${BlockSpacer} {
                    margin-top: 0;
                }
                &:last-child > ${BlockSpacer} {
                    margin-bottom: 0;
                }
            }
        `;
    }}

    ${props => props.containerCss}
    ${getVariantCss("containerCss")}
`;

const CENPDPBlock: React.FC<CENPDPBlockProps & React.HTMLAttributes<HTMLDivElement>> = ({
    children,
    margin = COMMON.spacing.content,
    variantProps = variantDefaultProps,
    ...props
}) => {
    if (!React.Children.count(children)) {
        return null;
    }

    const { blockProps, otherProps } = parseProps({ margin, variantProps, ...props });

    return (
        <BlockContainer {...otherProps} {...blockProps}>
            <BlockSpacer {...blockProps}>
                <BlockWrapper {...blockProps}>{children}</BlockWrapper>
            </BlockSpacer>
        </BlockContainer>
    );
};

CENPDPBlock.displayName = "Block";

export default CENPDPBlock;
